import { Link } from "react-router-dom";
import { ArrowRight, FlaskConical, Lightbulb, Sun } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const ingredients = [
  {
    id: "ginger",
    name: "Organic Ginger",
    tagline: "Nature's original anti-inflammatory",
    image: "https://images.unsplash.com/photo-1758055660285-d32b7b3b1e77?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    compounds: ["Gingerol", "Shogaol", "Zingerone"],
    paragraphs: [
      "Ginger has been used medicinally for over 5,000 years across Chinese, Indian, and Middle Eastern healing traditions — and modern science keeps validating what ancient healers knew. Its active compound, gingerol, is a potent anti-inflammatory and antioxidant that works at the cellular level to calm systemic inflammation, the root driver of most chronic discomfort.",
      "Beyond inflammation, ginger is one of the most researched natural remedies for digestion. It accelerates gastric emptying, eases bloating, and is clinically shown to relieve nausea — from morning queasiness to motion sickness. It also stimulates circulation and thermogenesis, which is why you feel that signature warmth spread through your chest seconds after a shot.",
    ],
    fact: "Studies suggest ginger can reduce exercise-induced muscle soreness by up to 25% when taken daily.",
  },
  {
    id: "lemon",
    name: "Organic Lemon",
    tagline: "Your daily vitamin C shield",
    image: "https://images.unsplash.com/photo-1624436518795-a2c1c77d6896?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    compounds: ["Vitamin C", "Citric Acid", "Hesperidin"],
    paragraphs: [
      "Lemon is far more than a bright flavor. Each fresh-squeezed serving delivers vitamin C — the nutrient your white blood cells burn through fastest when fighting off invaders. Because your body can't produce or store vitamin C, daily replenishment is one of the simplest, most proven ways to keep your immune defenses sharp.",
      "Lemon's flavonoids, like hesperidin, protect your cells from free-radical damage and support healthy blood vessels and skin collagen. And despite its sharp acidity, lemon has an alkalizing effect once metabolized — helping your body maintain the balanced internal pH where it functions best. Its citric acid also enhances mineral absorption, so you get more from every meal.",
    ],
    fact: "Vitamin C regenerates other antioxidants in your body — including vitamin E — multiplying your total antioxidant defense.",
  },
  {
    id: "honey",
    name: "Raw Honey",
    tagline: "The living superfood",
    image: "https://images.pexels.com/photos/8851478/pexels-photo-8851478.jpeg?auto=compress&cs=tinysrgb&w=800",
    compounds: ["Live Enzymes", "Bee Pollen", "Polyphenols"],
    paragraphs: [
      "Raw honey is fundamentally different from the pasteurized syrup on supermarket shelves. Because it's never heated or filtered, it keeps its live enzymes, bee pollen, propolis, and antioxidants intact — the very components that give honey its legendary healing reputation. Its natural hydrogen peroxide content makes it genuinely antibacterial and antifungal.",
      "Raw honey soothes sore throats and coughs as effectively as many over-the-counter remedies, and its balanced fructose-glucose profile delivers steady, clean energy without the spike-and-crash of refined sugar. In Ken's Gold, it also plays a practical role: rounding out the ginger fire so every shot finishes smooth.",
    ],
    fact: "Archaeologists have found pots of honey in ancient Egyptian tombs over 3,000 years old — still perfectly edible.",
  },
  {
    id: "acv",
    name: "Organic Apple Cider Vinegar",
    tagline: "The gut-reset with the 'mother'",
    image: "https://customer-assets.emergentagent.com/job_wellness-shots/artifacts/vy0md0kq_Home-Made-Apple-Cider-Vinegar-scaled.jpg",
    compounds: ["Acetic Acid", "The 'Mother'", "Probiotics"],
    paragraphs: [
      "Unfiltered apple cider vinegar contains the 'mother' — a living culture of beneficial bacteria, enzymes, and proteins formed during fermentation. This is where ACV's power lives. The mother feeds your gut microbiome, the ecosystem that governs not just digestion but immunity, mood, and even skin health.",
      "ACV's acetic acid is clinically shown to blunt blood sugar spikes after meals, improving insulin sensitivity and helping curb cravings — one reason it's a staple in healthy-weight routines. It also supports your body's natural detoxification pathways and stimulates stomach acid production, helping you break down and absorb nutrients more completely.",
    ],
    fact: "Taking ACV before a high-carb meal has been shown to reduce post-meal blood sugar response by up to 30%.",
  },
  {
    id: "greentea",
    name: "Organic Green Tea",
    tagline: "Calm, focused energy — no crash",
    image: "https://images.unsplash.com/photo-1668733521099-c120aa4ebbdc?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    compounds: ["EGCG", "L-Theanine", "Catechins"],
    paragraphs: [
      "Green tea is one of the most studied health beverages on Earth, and its star compound — EGCG — is among the most powerful antioxidants known. EGCG protects cells from oxidative damage, supports healthy metabolism, and has been linked to cardiovascular and brain health in decades of research.",
      "What makes green tea unique is its L-theanine, an amino acid that smooths caffeine's edges. Instead of jitters, you get what researchers call 'calm alertness' — sharpened focus and steady wakefulness that lasts for hours without a crash. It's the clean energy layer in every Ken's Gold shot, and the reason your afternoon doesn't fall apart.",
    ],
    fact: "The L-theanine + caffeine combination in green tea is shown to improve both attention and reaction time — while lowering stress markers.",
  },
];

const ritualTips = [
  { id: "morning", title: "Make it a morning ritual", text: "Take your shot first thing on an empty stomach. The ginger and lemon fire up digestion, and the green tea sets a focused tone for your entire day." },
  { id: "preworkout", title: "Before a workout", text: "20 minutes before training, a shot boosts circulation and thermogenesis — helping you warm up faster and recover easier." },
  { id: "immunity", title: "At first sign of a sniffle", text: "When your throat tickles, double down: honey soothes, vitamin C mobilizes your immune cells, and ginger calms inflammation." },
  { id: "consistency", title: "Consistency beats intensity", text: "The research behind every ingredient points to one thing: daily use. Small, steady doses compound into real change by week two." },
];

function WellnessHero() {
  return (
    <section className="hero-gradient text-center py-16 md:py-24 px-6" data-testid="wellness-hero">
      <p className="text-xs font-bold tracking-widest text-gold uppercase mb-3">The Wellness Guide</p>
      <h1 className="font-playfair text-4xl md:text-6xl font-bold text-brown-dark leading-tight mb-4" data-testid="wellness-title">
        Everything Inside <br /><span className="text-gold italic">Every Drop</span>
      </h1>
      <p className="text-gray-600 max-w-xl mx-auto text-base leading-relaxed">
        A deep dive into the five raw, organic ingredients in Ken's Gold — the compounds they carry, the science behind them, and how they work together in your body.
      </p>
    </section>
  );
}

function CompoundBadges({ compounds }) {
  return (
    <div className="flex flex-wrap gap-2 mb-5">
      {compounds.map((c) => (
        <span key={c} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold bg-honey-tint rounded-full px-3 py-1.5">
          <FlaskConical size={12} /> {c}
        </span>
      ))}
    </div>
  );
}

function IngredientDeepDive({ ing, index }) {
  const reversed = index % 2 === 1;
  return (
    <section className={`py-14 px-6 ${reversed ? "bg-cream" : ""}`} data-testid={`wellness-${ing.id}`}>
      <div className={`max-w-5xl mx-auto flex flex-col ${reversed ? "md:flex-row-reverse" : "md:flex-row"} gap-8 md:gap-12 items-start`}>
        <div className="md:w-64 flex-shrink-0 w-full">
          <img src={ing.image} alt={ing.name} className="w-full h-56 md:h-64 object-cover rounded-2xl" />
        </div>
        <div className="flex-1">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-brown-dark mb-1">{ing.name}</h2>
          <p className="text-gold text-sm font-semibold italic mb-4">{ing.tagline}</p>
          <CompoundBadges compounds={ing.compounds} />
          {ing.paragraphs.map((p) => (
            <p key={p.slice(0, 30)} className="text-gray-600 text-sm leading-relaxed mb-4">{p}</p>
          ))}
          <div className="flex items-start gap-3 bg-white border border-gold/15 rounded-xl px-5 py-4 mt-2" data-testid={`wellness-fact-${ing.id}`}>
            <Lightbulb size={16} className="text-gold mt-0.5 flex-shrink-0" />
            <p className="text-sm text-brown-dark"><strong>Did you know?</strong> <span className="text-gray-500">{ing.fact}</span></p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RitualSection() {
  return (
    <section className="py-16 px-6 bg-honey-tint" data-testid="wellness-ritual-section">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-widest text-gold uppercase mb-3">Get the Most From Every Shot</p>
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-brown-dark">Your Daily Wellness Ritual</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {ritualTips.map((tip) => (
            <div key={tip.id} className="bg-white rounded-2xl p-6 border border-gold/10" data-testid={`ritual-${tip.id}`}>
              <div className="flex items-center gap-2.5 mb-2.5">
                <Sun size={16} className="text-gold" />
                <h3 className="font-playfair font-bold text-brown-dark">{tip.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{tip.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WellnessCta() {
  return (
    <section className="cta-gradient py-16 md:py-20 px-6 text-center" data-testid="wellness-cta">
      <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white italic mb-4">Knowledge Is Good. A Daily Shot Is Better.</h2>
      <p className="text-white/70 text-base mb-8 max-w-xl mx-auto">All five ingredients, raw and organic, in one golden shot — ready when you are.</p>
      <Link to="/shop" className="btn-gold text-lg" data-testid="wellness-shop-btn">Shop the Shots <ArrowRight size={18} /></Link>
    </section>
  );
}

export default function Wellness() {
  useSEO({
    title: "Wellness Guide — Ingredient Benefits & Science",
    description: "The complete guide to Ken's Gold ingredients: organic ginger, organic lemon, raw honey, organic apple cider vinegar & organic green tea. Key compounds, science-backed benefits, and daily wellness tips.",
  });

  return (
    <div data-testid="wellness-page">
      <WellnessHero />
      {ingredients.map((ing, i) => (
        <IngredientDeepDive key={ing.id} ing={ing} index={i} />
      ))}
      <RitualSection />
      <WellnessCta />
    </div>
  );
}
